/* eslint-disable arrow-body-style */
import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {concatLatestFrom} from "@ngrx/operators";
import {Store} from "@ngrx/store";
import {MatSnackBar} from "@angular/material/snack-bar";
import {catchError, map, mergeMap, of} from "rxjs";
import {tap} from "rxjs/operators";

import {ErrorHandlingUtil} from "../../shared/utils/error-handling-util";
import {selectSelectedSourceDataset} from "../../import/store/import.selectors";
import {CommentApiService} from "../generated-sources/api/api/comment-api.service";
import {CommentActions, CommentCountsActions, CommentHistoryActions, CommentInboxActions, CommentThreadActions} from "./comment.actions";

export const loadThread = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentThreadActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{context}, dataset]) =>
                api.getComments(context, dataset).pipe(
                    map((comments) => CommentThreadActions.loadSuccess({context, comments})),
                    catchError((err: any) =>
                        of(CommentThreadActions.loadError({context, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const loadCounts = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentCountsActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{inContext}, dataset]) =>
                api.getCommentCounts(inContext, dataset).pipe(
                    map((counts) => CommentCountsActions.loadSuccess({counts})),
                    catchError((err: any) =>
                        of(CommentCountsActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

/** Cross-dataset — deliberately no `dataset` param (see prompt: inbox spans accessible datasets). */
export const loadInbox = createEffect(
    (
        actions$ = inject(Actions),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentInboxActions.load),
            mergeMap(() =>
                api.getCommentInbox().pipe(
                    map((inbox) => CommentInboxActions.loadSuccess({inbox})),
                    catchError((err: any) =>
                        of(CommentInboxActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const createComment = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentActions.create),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{context, request}, dataset]) =>
                api.createComment(request, dataset).pipe(
                    map((comment) => CommentActions.createSuccess({context, comment})),
                    catchError((err: any) =>
                        of(CommentActions.createError({context, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const updateComment = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentActions.update),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{id, context, request}, dataset]) =>
                api.updateComment(id, request, dataset).pipe(
                    map((comment) => CommentActions.updateSuccess({context, comment})),
                    catchError((err: any) =>
                        of(CommentActions.updateError({context, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const deleteComment = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentActions.delete),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{id, context}, dataset]) =>
                api.deleteComment(id, dataset).pipe(
                    map(() => CommentActions.deleteSuccess({id, context})),
                    catchError((err: any) =>
                        of(CommentActions.deleteError({context, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const resolveComment = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentActions.resolve),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{id, context}, dataset]) =>
                api.resolveComment(id, dataset).pipe(
                    map((comment) => CommentActions.resolveSuccess({context, comment})),
                    catchError((err: any) =>
                        of(CommentActions.resolveError({context, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const reopenComment = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentActions.reopen),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{id, context}, dataset]) =>
                api.reopenComment(id, dataset).pipe(
                    map((comment) => CommentActions.reopenSuccess({context, comment})),
                    catchError((err: any) =>
                        of(CommentActions.reopenError({context, errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

/** Any successful mutation refreshes the affected thread (and its badge count with it). */
export const reloadThreadAfterMutation = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                CommentActions.createSuccess,
                CommentActions.updateSuccess,
                CommentActions.deleteSuccess,
                CommentActions.resolveSuccess,
                CommentActions.reopenSuccess
            ),
            map(({context}) => CommentThreadActions.load({context}))
        );
    },
    {functional: true}
);

/** Creating / resolving / reopening / deleting can change what sits in the personal inbox. */
export const reloadInboxAfterMutation = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                CommentActions.createSuccess,
                CommentActions.deleteSuccess,
                CommentActions.resolveSuccess,
                CommentActions.reopenSuccess
            ),
            map(() => CommentInboxActions.load())
        );
    },
    {functional: true}
);

export const loadHistory = createEffect(
    (
        actions$ = inject(Actions),
        store$ = inject(Store),
        api = inject(CommentApiService)
    ) => {
        return actions$.pipe(
            ofType(CommentHistoryActions.load),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            mergeMap(([{id}, dataset]) =>
                api.getCommentHistory(id, dataset).pipe(
                    map((entries) => CommentHistoryActions.loadSuccess({entries})),
                    catchError((err: any) =>
                        of(CommentHistoryActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(err)}))
                    )
                )
            )
        );
    },
    {functional: true}
);

export const showMutationError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(
                CommentActions.createError,
                CommentActions.updateError,
                CommentActions.deleteError,
                CommentActions.resolveError,
                CommentActions.reopenError
            ),
            tap(({errorMsg}) => snackBar.open(errorMsg || "Comment action failed.", "", {duration: 3000}))
        );
    },
    {functional: true, dispatch: false}
);
